import { useState } from "react";
import logo from "../../logo.png";
import { parseApiJson } from "../../utils/parseApiJson";
import { API_URL } from "../dashboard/constants";
import "./LoginPage.css";

function getProfessionalError(rawMessage) {
  const message = String(rawMessage || "").toLowerCase();

  if (message.includes("incorrect email")) {
    return {
      title: "Email not found",
      body: "No account is registered with this email address. Please check the email or contact your administrator.",
    };
  }

  if (message.includes("incorrect password")) {
    return {
      title: "Incorrect password",
      body: "The password you entered is incorrect. Please try again.",
    };
  }

  if (message.includes("deactivated")) {
    return {
      title: "Account deactivated",
      body: "Your account has been deactivated. Please contact your administrator to regain access.",
    };
  }

  if (message.includes("only for co-owner or manager")) {
    return {
      title: "Access restricted",
      body: "Web portal login is available only for co-owner or manager accounts.",
    };
  }

  if (message.includes("database is offline")) {
    return {
      title: "Service temporarily unavailable",
      body: "We are unable to sign you in right now because the database is offline. Please try again shortly.",
    };
  }

  if (message.includes("unable to reach server")) {
    return {
      title: "Connection issue",
      body: "We could not connect to the server. Please check your internet or try again in a moment.",
    };
  }

  if (message.includes("received a web page instead of api")) {
    return {
      title: "API address misconfigured",
      body: "The app is calling the wrong server. In frontend/.env set REACT_APP_API_URL=http://localhost:5000 (backend), not port 3000. Restart the dev server and ensure the API is running.",
    };
  }

  return {
    title: "Login failed",
    body: "We could not sign you in at the moment. Please verify your credentials and try again.",
  };
}

function isLikelyValidEmail(value) {
  const email = String(value || "").trim().toLowerCase();
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email);
}

function LoginPage({ onLoginSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  async function handleSubmit(event) {
    event.preventDefault();
    setError(null);

    if (!email.trim() || !password.trim()) {
      setError({
        title: "Missing credentials",
        body: "Please enter both email and password to continue.",
      });
      return;
    }

    if (!isLikelyValidEmail(email)) {
      setError({
        title: "Invalid email format",
        body: "Please enter a valid email address (for example: name@example.com).",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          password: password.trim(),
          clientType: "web",
        }),
      });
      const payload = await parseApiJson(response);
      if (!response.ok) {
        throw new Error(payload?.message || "Login failed.");
      }
      onLoginSuccess(payload);
    } catch (loginError) {
      if (loginError?.name === "TypeError") {
        setError(getProfessionalError("unable to reach server"));
      } else {
        setError(getProfessionalError(loginError.message || "login failed"));
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <main className="login-shell">
      <section className="login-card">
        <img className="login-logo" src={logo} alt="Worktivity" />
        <p>Sign in to access your web portal dashboard.</p>

        <form onSubmit={handleSubmit} className="login-form">
          <label>
            Email
            <input
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="you@company.com"
              required
            />
          </label>

          <label>
            Password
            <div className="password-field">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                placeholder="••••••••"
                required
              />
              <button
                type="button"
                className="toggle-password-btn"
                onClick={() => setShowPassword((prev) => !prev)}
                aria-label={showPassword ? "Hide password" : "Show password"}
                title={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? (
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M3 3l18 18" />
                    <path d="M10.58 10.58a2 2 0 002.83 2.83" />
                    <path d="M9.88 5.09A10.9 10.9 0 0112 5c5.05 0 9.27 3.11 10.8 7.5a11.83 11.83 0 01-3.26 4.76" />
                    <path d="M6.61 6.61A11.84 11.84 0 001.2 12.5 11.8 11.8 0 005.1 17.86" />
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M1.2 12.5C2.73 8.11 6.95 5 12 5s9.27 3.11 10.8 7.5C21.27 16.89 17.05 20 12 20S2.73 16.89 1.2 12.5z" />
                    <circle cx="12" cy="12.5" r="3" />
                  </svg>
                )}
              </button>
            </div>
          </label>

          {error ? (
            <div className="login-error" role="alert" aria-live="assertive">
              <strong>{error.title}</strong>
              <p>{error.body}</p>
            </div>
          ) : null}

          <button type="submit" className="login-submit-btn" disabled={isSubmitting}>
            {isSubmitting ? "Signing in..." : "Login"}
          </button>
        </form>
      </section>
    </main>
  );
}

export default LoginPage;
